import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useUser } from "@supabase/auth-helpers-react";
import { ArrowRight, ArrowLeft, ExternalLink, Building, Loader2 } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLoadScript } from "@react-google-maps/api";
import AddressAutocomplete from "@/components/AddressAutocomplete";

// Country codes data with labels
const countryOptions = {
  'PT': { code: '+351', label: 'Portugal' },
  'DE': { code: '+49', label: 'Germany' },
  'FR': { code: '+33', label: 'France' },
  'GB': { code: '+44', label: 'United Kingdom' },
  'ES': { code: '+34', label: 'Spain' },
  'IT': { code: '+39', label: 'Italy' },
  'IE': { code: '+353', label: 'Ireland' },
  'BE': { code: '+32', label: 'Belgium' },
  'NL': { code: '+31', label: 'Netherlands' },
  'AT': { code: '+43', label: 'Austria' },
  'CH': { code: '+41', label: 'Switzerland' },
  'US': { code: '+1', label: 'USA' },
  'SI': { code: '+386', label: 'Slovenia' },
} as const;

// Define the form schema with new address fields
const setupAccountSchema = z.object({
  business_name: z.string().min(2, "Business name is required"),
  business_email: z.string().email("Invalid email address"),
  business_phone: z.string().min(1, "Phone number is required"),
  extension: z.string(),
  street_address: z.string().min(5, "Street address is required"),
  address_line_2: z.string().optional(),
  postal_code: z.string().min(3, "Postal code is required"),
  city: z.string().min(2, "City is required"),
  region: z.string().optional(),
  country: z.string().min(2, "Country is required"),
  business_tax_id: z.string(),
  business_iban: z.string(),
  business_swift: z.string(),
  bank_name: z.string(),
});

type SetupAccountFormData = z.infer<typeof setupAccountSchema>;

// 1. Fix the Google Maps libraries issue with correct typing
const GOOGLE_MAPS_LIBRARIES = ["places"] as ["places"];

const SetupAccount = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const navigate = useNavigate();
  const user = useUser();
  const { t } = useTranslation();

  // Get the step from URL query parameters and validate it
  const location = window.location;
  const params = new URLSearchParams(location.search);
  const stepParam = params.get('step');

  // Define the maximum step index (0-based, so for 3 steps, max is 2)
  const MAX_STEP = 2;

  // Parse step parameter and ensure it's within valid range
  const initialStep = stepParam ? Math.min(parseInt(stepParam), MAX_STEP) : 0;
  const [currentStep, setCurrentStep] = useState(initialStep);

  // Update URL when step changes
  useEffect(() => {
    const newParams = new URLSearchParams(location.search);
    newParams.set('step', currentStep.toString());
    const newUrl = `${location.pathname}?${newParams.toString()}`;
    window.history.replaceState({}, '', newUrl);
  }, [currentStep, location.pathname, location.search]);

  const steps = [t("account_setup.business_details"), t("account_setup.payment_setup"), t("account_setup.stripe_setup")];


  const form = useForm<SetupAccountFormData>({
    resolver: zodResolver(setupAccountSchema),
    defaultValues: {
      business_name: "",
      business_email: "",
      business_phone: "",
      extension: '+351', // Default to Portuguese code
      street_address: "",
      address_line_2: "",
      postal_code: "",
      city: "",
      region: "",
      country: "PT", // Default to Portugal
      business_tax_id: "",
      business_iban: "",
      business_swift: "",
      bank_name: "",
    },
  });

  // 2. Update the LoadScript call to use the static libraries array
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  // 3. Add a more robust form persistence approach
  // This effect runs once on mount to load saved form state
  useEffect(() => {
    const savedState = sessionStorage.getItem('setupAccountFormState');
    if (savedState) {
      try {
        const parsedState = JSON.parse(savedState);
        // Use setTimeout to ensure this runs after initial render
        setTimeout(() => {
          Object.entries(parsedState).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              form.setValue(key as any, value);
            }
          });
        }, 0);
      } catch (error) {
        console.error("Error parsing saved form state:", error);
      }
    }
  }, []);

  // Add this effect to handle tab visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Force a re-render when tab becomes visible again
        const savedFormState = sessionStorage.getItem('setupAccountFormState');
        if (savedFormState) {
          try {
            const parsedState = JSON.parse(savedFormState);

            // Special handling for address fields
            // First set the address fields directly
            if (parsedState.street_address) {
              form.setValue('street_address', parsedState.street_address);
            }
            if (parsedState.address_line_2) {
              form.setValue('address_line_2', parsedState.address_line_2);
            }
            if (parsedState.city) {
              form.setValue('city', parsedState.city);
            }
            if (parsedState.region) {
              form.setValue('region', parsedState.region);
            }
            if (parsedState.postal_code) {
              form.setValue('postal_code', parsedState.postal_code);
            }
            if (parsedState.country) {
              form.setValue('country', parsedState.country);
            }

            // Then set all other fields
            Object.entries(parsedState).forEach(([key, value]) => {
              // Skip address fields as we've already handled them
              if (!['street_address', 'address_line_2', 'city', 'region', 'postal_code', 'country'].includes(key)) {
                if (value !== undefined && value !== null) {
                  form.setValue(key as any, value);
                }
              }
            });

            // Force form validation after setting values
            form.trigger();
          } catch (error) {
            console.error("Error parsing saved form state:", error);
          }
        }
      }
    };

    // Add event listener for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Clean up
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [form]);

  // Use a subscription to watch form changes and save to sessionStorage
  useEffect(() => {
    const subscription = form.watch((value) => {
      sessionStorage.setItem('setupAccountFormState', JSON.stringify(value));
    });

    return () => subscription.unsubscribe();
  }, [form]);

  // Modify your existing useEffect to prioritize form data over empty database fields
  useEffect(() => {
    const fetchBusinessProfile = async () => {
      if (!user) return;

      // Get current form values before fetching
      const currentFormValues = form.getValues();
      const hasFormData = Object.values(currentFormValues).some(val =>
        val !== undefined && val !== null && val !== "");

      // Always fetch from database first
      const { data, error } = await supabase
        .from("business_profiles")
        .select("business_name, business_email, business_phone, business_tax_id, business_iban, bank_name, business_swift, country, street_address, address_line_2, postal_code, city, region, extension")
        .eq("user_id", user.id)
        .single();

      if (error) {
        console.error("Error fetching business profile:", error);

        // Only try sessionStorage as fallback if database fetch fails
        const savedState = sessionStorage.getItem('setupAccountFormState');
        if (savedState) {
          try {
            const parsedState = JSON.parse(savedState);
            Object.entries(parsedState).forEach(([key, value]) => {
              if (value) {
                form.setValue(key as any, value);
              }
            });
          } catch (error) {
            console.error("Error parsing saved form state:", error);
          }
        }
        return;
      }

      if (data) {
        // Set form values individually, but only if the database value is not empty
        // or if the current form value is empty
        Object.entries(data).forEach(([key, value]) => {
          // Only process fields that exist in our form
          if (key in currentFormValues) {
            const currentValue = currentFormValues[key as keyof typeof currentFormValues];
            // Only set the value if:
            // 1. The database value is not empty/null/undefined AND
            // 2. Either the current form value is empty OR we don't have any form data yet
            if (value && (currentValue === "" || currentValue === null || currentValue === undefined || !hasFormData)) {
              form.setValue(key as any, value);
            }
          }
        });

        // Special handling for country code
        if (data.country && data.country in countryOptions &&
          (!currentFormValues.extension || !hasFormData)) {
          form.setValue("extension", countryOptions[data.country as keyof typeof countryOptions].code);
        } else if (data.extension && (!currentFormValues.extension || !hasFormData)) {
          form.setValue("extension", data.extension);
        }
      } else if (!hasFormData) {
        // No database data and no form data, try sessionStorage as fallback
        const savedState = sessionStorage.getItem('setupAccountFormState');
        if (savedState) {
          try {
            const parsedState = JSON.parse(savedState);
            Object.entries(parsedState).forEach(([key, value]) => {
              if (value) {
                form.setValue(key as any, value);
              }
            });
          } catch (error) {
            console.error("Error parsing saved form state:", error);
          }
        }
      }
    };

    fetchBusinessProfile();
  }, [user]);

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setIsLoading(true);
      const file = event.target.files?.[0];
      if (!file || !user) return;

      if (!file.type.startsWith("image/")) {
        toast.error(t("toasts.upload_image_file"));
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast.error(t("toasts.file_size_warning"));
        return;
      }

      const fileExt = file.name.split(".").pop();
      const filePath = `${user.id}/${crypto.randomUUID()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("business_logos")
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: publicUrl } = supabase.storage
        .from("business_logos")
        .getPublicUrl(filePath);

      setLogoUrl(publicUrl.publicUrl);
      toast.success(t("toasts.logo_upload_success"));
    } catch (error: any) {
      console.error("Logo upload error:", error);
      toast.error(t("toasts.logo_error_upload"), {
        description: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const saveBusinessProfile = async (data: SetupAccountFormData) => {
    if (!user) {
      toast.error(t("toasts.login_for_setup"));
      return;
    }

    const { error: profileError } = await supabase
      .from("business_profiles")
      .update({
        business_name: data.business_name,
        business_email: data.business_email,
        business_phone: data.business_phone.replace(/^\+/, ''),
        street_address: data.street_address,
        address_line_2: data.address_line_2,
        postal_code: data.postal_code,
        city: data.city,
        region: data.region,
        country: data.country,
        business_tax_id: data.business_tax_id,
        business_iban: data.business_iban,
        business_swift: data.business_swift,
        bank_name: data.bank_name,
        logo_url: logoUrl,
        extension: data.extension,
      })
      .eq("user_id", user.id);

    if (profileError) throw profileError;
    toast.success(t("toasts.business_profile_saved_success"));

    // Clear the saved form state after successful save
    sessionStorage.removeItem('setupAccountFormState');
  };

  const handleCreateStripeAccount = async () => {
    try {
      setIsLoading(true);
      if (!user) {
        toast.error(t("toasts.login_for_setup"));
        return;
      }

      // Get business profile data including business_type and country
      const { data: profileData, error: profileError } = await supabase
        .from("business_profiles")
        .select("business_type, country")
        .eq("user_id", user.id)
        .single();

      if (profileError) throw profileError;

      // Create Stripe account
      const { data: stripeResponse, error: stripeError } = await supabase.functions.invoke(
        "create-stripe-account",
        {
          body: {
            business_type: profileData.business_type,
            country: profileData.country,
            email: user.email,
          },
        }
      );

      if (stripeError) throw stripeError;

      // Get onboarding URL
      const { data: urlData, error: urlError } = await supabase.functions.invoke(
        "get-stripe-onboarding-url",
        { body: { accountId: stripeResponse.accountId } }
      );

      if (urlError) throw urlError;

      toast.success(t("toasts.stripe_created_success"));

      // Open Stripe onboarding in new tab and navigate to dashboard
      if (urlData?.url) {
        window.open(urlData.url, '_blank');
      }
      navigate("/dashboard");
    } catch (error: any) {
      console.error("Setup error:", error);
      toast.error(t("toasts.stripe_setup_error"), {
        description: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnectStripeAccount = async () => {
    try {
      setIsLoading(true);
      const { data: urlData, error: urlError } = await supabase.functions.invoke(
        "connect-stripe-account",
        { body: {} }
      );

      if (urlError) throw urlError;
      if (urlData?.url) {
        window.open(urlData.url, '_blank');
        navigate("/dashboard");
      } else {
        throw new Error('No connection URL received');
      }
    } catch (error: any) {
      console.error("Error connecting Stripe account:", error);
      toast.error(t("toasts.stripe_connect_error"), {
        description: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkipStripe = async () => {
    try {
      setIsLoading(true);
      if (!user) {
        toast.error(t("toasts.login_for_setup"));
        return;
      }

      const { error } = await supabase
        .from("business_profiles")
        .update({ stripe_setup_skipped: true })
        .eq("user_id", user.id);

      if (error) throw error;

      toast.success(t("toasts.setup_complete_stripe_later"));
      navigate("/dashboard");
    } catch (error: any) {
      console.error("Error skipping Stripe setup:", error);
      toast.error(t("toasts.error_completing_setup"), {
        description: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const next = async () => {
    try {
      const isValid = await form.trigger();
      if (!isValid) {
        toast.error(t("toasts.fill_required_fields_correctly"));
        return;
      }

      if (currentStep === 1) {
        setIsLoading(true);
        await saveBusinessProfile(form.getValues());
        setCurrentStep(prev => prev + 1);
      } else {
        setCurrentStep(prev => prev + 1);
      }
    } catch (error: any) {
      console.error("Error moving to next step:", error);
      toast.error(t("toasts.saving_profile_error"), {
        description: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const back = () => {
    setCurrentStep(prev => prev - 1);
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <>
            <p className="text-sm font-normal text-muted-foreground mb-6">{t("account_setup.fields_with_asterisk")}</p>

            <div className="flex items-center space-x-4 mb-6">
              <Avatar className="w-20 h-20">
                <AvatarImage src={logoUrl || ''} alt="Business logo" className="object-contain bg-background" />
                <AvatarFallback className="text-muted-foreground bg-[#5CC2BB]">
                  <Building className="h-10 w-10" />
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  disabled={isLoading}
                  className="h-auto py-2 bg-white border border-gray-300 p-2 rounded-md"
                />
                <p className="text-sm font-thin text-muted-foreground mt-1">
                  {t("account_settings.recommended_image")}
                </p>
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-4 text-left">
              <FormField
                control={form.control}
                name="business_name"
                render={({ field }) => (
                  <FormItem className="w-full md:flex-1">
                    <FormLabel className="text-[#8791F3]">{t("seller_info.business_name", "Business Name")}*</FormLabel>
                    <FormControl>
                      <Input className="bg-white border border-gray-300 p-2 rounded-md" placeholder={t("seller_info.business_name_placeholder", "Happy Co.")} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="business_email"
                render={({ field }) => (
                  <FormItem className="w-full md:flex-1">
                    <FormLabel className="text-[#8791F3]">{t("seller_info.email", "Email")}*</FormLabel>
                    <FormControl>
                      <Input className="bg-white border border-gray-300 p-2 rounded-md" placeholder={t("seller_info.email_placeholder", "contact@gmail.com")} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="extension"
                render={({ field }) => (
                  <FormItem className="w-full md:w-24">
                    <FormLabel>{t("seller_info.extension", "Extension")}*</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger className="text-[#34375B] font-normal bg-white border border-gray-300 p-2 rounded-md">
                          <SelectValue placeholder={t("seller_info.extension_placeholder", "Select extension")} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="bg-white border border-gray-300 p-2 rounded-md">
                        {Object.entries(countryOptions).map(([country, { code }]) => (
                          <SelectItem key={country} value={code}>
                            {code}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="business_phone"
                render={({ field }) => (
                  <FormItem className="w-full md:w-36">
                    <FormLabel className="text-[#8791F3]">{t("seller_info.phone", "Phone")}*</FormLabel>
                    <FormControl>
                      <Input
                        className="bg-white border border-gray-300 p-2 rounded-md"
                        placeholder="123456789"
                        {...field}
                        onChange={(e) => {
                          const noSpaces = e.target.value.replace(/\s+/g, '');
                          field.onChange(noSpaces);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex flex-col md:flex-row gap-4 text-left">
              <FormField
                control={form.control}
                name="street_address"
                render={({ field }) => (
                  <FormItem className="w-full md:flex-[2] md:max-w-[448px]">
                    <FormLabel className="text-[#8791F3]">{t("account_settings.address_line_1", "Street Name")}*</FormLabel>
                    <FormControl>
                      {isLoaded ? (
                        <AddressAutocomplete
                          value={field.value}
                          onChange={(address, details) => {
                            field.onChange(address);

                            // Auto-fill other address fields if details are available
                            if (details) {
                              // Set postal code
                              if (details.postalCode) {
                                form.setValue('postal_code', details.postalCode);
                              }

                              // Set city
                              if (details.city) {
                                form.setValue('city', details.city);
                              }

                              // Set region/state
                              if (details.region) {
                                form.setValue('region', details.region);
                              }

                              // Set country - use the country code to select from countryOptions
                              if (details.country) {
                                // Check if the country code exists in countryOptions
                                if (Object.prototype.hasOwnProperty.call(countryOptions, details.country)) {
                                  console.log("DETAILS", details);
                                  console.log("Details country", details.country);
                                  // Set the country value to the country code itself
                                  form.setValue("country", details.country);

                                  // Force the form to update
                                  form.trigger("country");
                                } else {
                                  console.warn(`Country code ${details.country} not found in countryOptions`);
                                }
                              }
                            }
                          }}
                          className="bg-white border border-gray-300 p-2 rounded-md"
                          placeholder={t("buyer_info.client_address_placeholder", "221B Baker Street")}
                        />
                      ) : (
                        <Input
                          className="bg-white border border-gray-300 p-2 rounded-md"
                          placeholder={t("buyer_info.client_address_placeholder", "221B Baker Street")}
                          {...field}
                        />
                      )}
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="address_line_2"
                render={({ field }) => (
                  <FormItem className="w-full md:flex-1">
                    <FormLabel className="text-[#8791F3]">{t("account_settings.address_line_2", "Address Line 2")}</FormLabel>
                    <FormControl>
                      <Input className="bg-white border border-gray-300 p-2 rounded-md" placeholder={t("account_settings.address_line_2_placeholder", "Suite 100 (Optional)")} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* <div className="flex flex-col md:flex-row gap-4 text-left">
              <FormField
                control={form.control}
                name="address_line_2"
                render={({ field }) => (
                  <FormItem className="w-full md:flex-[2] md:max-w-[440px]">
                    <FormLabel className="text-[#8791F3]">{t("account_settings.address_line_2", "Address Line 2")}</FormLabel>
                    <FormControl>
                      <Input className="bg-white border border-gray-300 p-2 rounded-md" placeholder={t("account_settings.address_line_2_placeholder", "Suite 100 (Optional)")} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="region"
                render={({ field }) => (
                  <FormItem className="w-full md:flex-1">
                    <FormLabel className="text-[#8791F3]">{t("account_settings.region", "Region/State")}</FormLabel>
                    <FormControl>
                      <Input className="bg-white border border-gray-300 p-2 rounded-md" placeholder={t("account_settings.region_placeholder", "Region (Optional)")} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div> */}

            <div className="flex flex-col md:flex-row gap-4 text-left">
              <FormField
                control={form.control}
                name="region"
                render={({ field }) => (
                  <FormItem className="w-full md:flex-1">
                    <FormLabel className="text-[#8791F3]">{t("account_settings.region", "Region/State")}</FormLabel>
                    <FormControl>
                      <Input className="bg-white border border-gray-300 p-2 rounded-md" placeholder={t("account_settings.region_placeholder", "Region (Optional)")} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="postal_code"
                render={({ field }) => (
                  <FormItem className="w-full md:flex-1">
                    <FormLabel className="text-[#8791F3]">{t("account_settings.postal_code", "Postal Code")}*</FormLabel>
                    <FormControl>
                      <Input className="bg-white border border-gray-300 p-2 rounded-md" placeholder={t("account_settings.postal_code_placeholder", "1234-567")} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem className="w-full md:w-40">
                    <FormLabel className="text-[#8791F3]">{t("account_settings.city", "City")}*</FormLabel>
                    <FormControl>
                      <Input className="bg-white border border-gray-300 p-2 rounded-md" placeholder={t("account_settings.city", "City")} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="country"
                render={({ field }) => (
                  <FormItem className="w-full md:flex-1">
                    <FormLabel className="text-[#8791F3]">{t("account_settings.country", "Country")}*</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger className="text-[#34375B] font-normal bg-white border border-gray-300 p-2 rounded-md">
                          <SelectValue placeholder={t("account_settings.country_select", "Select a country")} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="bg-white border border-gray-300 p-2 rounded-md">
                        {Object.entries(countryOptions).map(([code, { label }]) => (
                          <SelectItem key={code} value={code}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

          </>
        );
      case 1:
        return (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
              <FormField
                control={form.control}
                name="business_tax_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[#8791F3]">{t("seller_info.tax_id", "Tax ID")}*</FormLabel>
                    <FormControl>
                      <Input className="bg-white border border-gray-300 p-2 rounded-md" placeholder={t("seller_info.tax_id_number", "Tax ID number")} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="bank_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[#8791F3]">{t("seller_info.bank_name", "Bank Name")}*</FormLabel>
                    <FormControl>
                      <Input className="bg-white border border-gray-300 p-2 rounded-md" placeholder={t("seller_info.bank_name_placeholder", "Bank name")} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
              <FormField
                control={form.control}
                name="business_iban"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[#8791F3]">{t("seller_info.iban", "IBAN")}*</FormLabel>
                    <FormControl>
                      <Input
                        className="bg-white border border-gray-300 p-2 rounded-md" placeholder={t("seller_info.iban", "IBAN number")}
                        {...field}
                        onChange={(e) => {
                          const noSpaces = e.target.value.replace(/\s+/g, '');
                          field.onChange(noSpaces);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="business_swift"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[#8791F3]">{t("seller_info.swift_bic", "SWIFT/BIC")}*</FormLabel>
                    <FormControl>
                      <Input className="bg-white border border-gray-300 p-2 rounded-md" placeholder={t("seller_info.swift_bic_code", "SWIFT/BIC code")} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </>
        );
      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center space-y-4">
              <h3 className="text-lg font-normal">{t("account_setup.setup_payments_stripe")}</h3>
              <p className="text-sm font-thin text-muted-foreground">
                {t("account_setup.choose_how_handle_payments")}
              </p>
            </div>

            <div className="flex justify-center">
              <Button
                onClick={handleCreateStripeAccount}
                disabled={isLoading}
                className="w-full sm:w-2/3 md:w-2/5 flex items-center justify-center bg-[#8791F3] hover:bg-[#5CC2BB] hover:text-[#34375B] hover:shadow-[4px_4px_0px_0px_#34375B] hover:-translate-x-1 hover:-translate-y-1 transition-all duration-200"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t("auth.signing_up")}
                  </>
                ) : (
                  <>
                    {t("account_setup.create_new_stripe_account")}<ExternalLink className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
            </div>

            <div className="text-center">
              <Button
                variant="link"
                onClick={handleSkipStripe}
                disabled={isLoading}
                className="text-sm"
              >
                {t("account_setup.do_this_later")}
              </Button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };


  return (
    <div className="container mx-auto px-4 sm:px-6 py-4 sm:py-6">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6 sm:mb-8">
          <h1 className="font-archivoblack text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 text-[#34375B] text-center sm:text-left">{t("account_setup.complete_account_setup")}</h1>
          <div className="flex justify-between items-center">
            {[1, 2, 3].map((step, index) => (
              <div
                key={step}
                className={`flex items-center ${index !== 2 ? "flex-1" : ""}`}
              >
                <div
                  className={`flex items-center justify-center w-6 h-6 sm:w-8 sm:h-8 rounded-full border-2 ${currentStep >= index
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-muted text-muted-foreground"
                    }`}
                >
                  <span className="text-xs sm:text-sm">{step}</span>
                </div>
                {index !== 2 && (
                  <div
                    className={`flex-1 h-0.5 mx-1 sm:mx-2 ${currentStep > index ? "bg-primary" : "bg-muted"}`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        <Card className="shadow-sm">
          <CardHeader className="px-4 sm:px-6 py-4 sm:py-6">
            <CardTitle className="text-[#34375B] text-lg sm:text-xl text-center sm:text-left">{steps[currentStep]}</CardTitle>
          </CardHeader>
          <CardContent className="px-4 sm:px-6 pb-6">
            <Form {...form}>
              <form className="space-y-4 sm:space-y-6">
                {renderStepContent()}

                {currentStep !== 2 && (
                  <div className="flex flex-col sm:flex-row justify-between gap-3 sm:gap-0 pt-4">
                    <Button
                      className="text-[#F1F6F5] hover:border-0 bg-[#8791F3] hover:text-[#34375B] hover:bg-[#5CC2BB] hover:shadow-[4px_4px_0px_0px_#34375B] hover:-translate-x-1 hover:-translate-y-1 transition-all duration-200 w-full sm:w-auto order-2 sm:order-1"
                      type="button"
                      onClick={back}
                      disabled={currentStep === 0 || isLoading}
                      variant="outline"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      {t("hero.back")}
                    </Button>
                    <Button
                      className="text-[#F1F6F5] bg-[#8791F3] hover:text-[#34375B] hover:bg-[#5CC2BB] hover:shadow-[4px_4px_0px_0px_#34375B] hover:-translate-x-1 hover:-translate-y-1 transition-all duration-200 w-full sm:w-auto order-1 sm:order-2"
                      type="button"
                      onClick={next}
                      disabled={isLoading}
                    >
                      {currentStep === 1 ? t("account_setup.save_and_continue") : t("account_setup.next")}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                )}
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SetupAccount;
